import { STRINGS } from 'config';
import { KJUR } from 'jsrsasign';
import { OrgDetails } from 'model/OrgDetails';
import { Dispatch } from 'react';
import request from 'superagent';
import { identifyUserInFreshpaint } from 'util/freshpaint';
import { updateIntercom } from 'util/intercom';
import { getFeatureFlags } from './featureFlags';

const {
  SUMMARISER,
  AUTH,
  USER_MANAGEMENT: { HOST: USER_MG_HOST },
  REDUCERS: {
    APP: { SET_USER_DATA },
    AUTH: {
      API_CHECK_AUTHENTICATION_REQUEST,
      API_CHECK_AUTHENTICATION_SUCCESS,
      CHECK_AUTHENTICATION_ERROR,
      API_REFRESH_SUCCESS,
      DEV_LOGIN_SUCCESS,
      DEV_LOGIN_ERROR,
    },
  },
  TOKEN: { JWT_AUTH_SECRET },
} = STRINGS;

const color = 'colorExpressionExpression1';

const getOrgName = (id: string, orgDetails: OrgDetails[]) => {
  if (orgDetails) {
    return orgDetails.find((ea: OrgDetails) => ea.id === id)?.prettyName;
  }
  return id;
};

const getOrgPcmCode = (id: string, orgDetails: OrgDetails[]) =>
  orgDetails?.find((ea: OrgDetails) => ea.id === id)?.pcmCode;

const getOrgClinicalSystem = (id: string, orgDetails: OrgDetails[]) =>
  orgDetails?.find((ea: OrgDetails) => ea.id === id)?.clinicalSystemId;

export const redirectUserToLogoutScreen = () => {
  window.location.href = `${AUTH.HOST}/logout?redirect=${window.location.href}`;
};

export const redirectUserToLoginScreen = () => {
  window.location.href = `${AUTH.HOST}/remote?callbackUrl=${window.location.href}`;
};

export const getCurrentUser = (dispatch: Dispatch<unknown>) => {
  dispatch({ type: API_CHECK_AUTHENTICATION_REQUEST });
  return request
    .get(`${USER_MG_HOST}/users/current`)
    .withCredentials()
    .then(resp => {
      dispatch({ type: API_CHECK_AUTHENTICATION_SUCCESS });
      const { sub: userId, orgIds, email, orgDetails, nickname, roles, name } = resp?.body || {};
      const { practiceIds, ehubIds, admin, summariser } = orgIds;
      if (!practiceIds && !ehubIds) {
        redirectUserToLogoutScreen();
        return false;
      }
      const practices = practiceIds
        ? practiceIds.map((ea: string) => ({
            prettyName: getOrgName(ea, orgDetails),
            pcmCode: getOrgPcmCode(ea, orgDetails),
            id: ea,
            color,
            clinicalSystemId: getOrgClinicalSystem(ea, orgDetails),
          }))
        : [];
      const ehubs = ehubIds
        ? ehubIds.map((ea: string) => ({
            prettyName: getOrgName(ea, orgDetails),
            id: ea,
            color,
          }))
        : [];
      const adminRights = admin || [];
      if (email) {
        updateIntercom({
          email,
          user_id: email,
          name: email,
          companies: [...practices, ...ehubs].map((ea: { id: string }) => ({ id: ea.id, name: ea.id })),
        });
      }
      const selectedPractice = practices[0] || ehubs[0];
      const summariserIds = summariser; // used for unlimited sub check
      dispatch({
        type: SET_USER_DATA,
        payload: {
          userId,
          practices,
          ehubs,
          adminRights,
          currentUserEmail: email,
          selectedPractice,
          roles,
          name: name || email,
          summariserIds,
        },
      });
      identifyUserInFreshpaint(userId, { email, name: nickname, workflowFeVersion: process.env.REACT_APP_VERSION });
      getFeatureFlags(selectedPractice?.id)(dispatch);
      return true;
    })
    .catch(err => {
      dispatch({ type: CHECK_AUTHENTICATION_ERROR, payload: err });
      redirectUserToLoginScreen();
      return false;
    });
};

export function refreshUserSession(dispatch: Dispatch<unknown>) {
  return request
    .get(`${AUTH.HOST}/oauth/access_token`)
    .withCredentials()
    .then(() => {
      dispatch({ type: API_REFRESH_SUCCESS });
      return true;
    })
    .catch(error => {
      dispatch({ type: CHECK_AUTHENTICATION_ERROR, payload: error });
      // If error is 401 then refresh token is invalid or 400 if there is no refresh token, so redirect user to login
      // other errors would indicate a network problem that might be temporary, so allow the user to continue
      if (error.status === 401 || error.status === 400) {
        console.log('Error causing redirect ', error);
        redirectUserToLoginScreen();
        return false;
      }
      console.log('Error not causing redirect ', error);
      return true;
    });
}

interface LoginDevParams {
  userName: string;
  practiceId: string;
  clinicalSystem: string;
  dispatch: React.Dispatch<{ type: string; payload?: unknown }>;
  identity: string;
}

export const loginDev = ({
  userName,
  practiceId,
  clinicalSystem,
  dispatch,
  identity,
}: LoginDevParams): Promise<void> => {
  const header = JSON.stringify({ alg: 'HS256', typ: 'JWT' });
  const payload = JSON.stringify({
    iss: 'eConsult',
    sub: userName,
    practiceId,
    clinicalSystem,
    iat: KJUR.jws.IntDate.get('now'),
    exp: KJUR.jws.IntDate.get('now + 1day'),
  });
  const token = KJUR.jws.JWS.sign('HS256', header, payload, JWT_AUTH_SECRET);

  const requestBody = {
    token,
    ...(identity !== STRINGS.USER.IDENTITY.NONE ? { identity } : {}),
  };

  return request
    .post(`${AUTH.HOST}/session`)
    .send(requestBody)
    .withCredentials()
    .then(() => {
      dispatch({ type: DEV_LOGIN_SUCCESS });
      window.location.href = `${SUMMARISER.BASE_URL}`;
    })
    .catch((error: unknown) => {
      dispatch({ type: DEV_LOGIN_ERROR, payload: error });
    });
};
